// Interior photography — Unsplash CDN, curated for visual richness.
// Apple-style demands real photography, not SVG placeholders.
//
// All URLs go through one base so the CDN can be swapped in one place
// (CLAUDE.md §1 — no hardcoded literals scattered through components).

import { env } from "../lib/env";

// Unsplash photo IDs — chosen for warm, scandinavian-indo interior tone.
// If a photo ever 404s, the <img> onError handler in the consumer falls
// back to a brand-coloured gradient.
const UNSPLASH_PHOTOS = {
  livingSofa:      "1555041469-a586c61ea9bc",
  bedroom:         "1505693416388-ac5ce068fe85",
  kitchenModern:   "1556909114-f6e7ad7d3136",
  kitchenIsland:   "1556909212-d5b604d0c90d",
  diningWarm:      "1567538096630-e0c55bd6374c",
  workspace:       "1493663284031-b7e3aefcae8e",
  bathroomLuxe:    "1552321554-5fefe8c9ef14",
  kidsRoom:        "1558211583-d26f610c1eb1",
  chandelier:      "1540932239986-30128078f3c5",
  wardrobe:        "1631889993959-41b4e9c6e3c5",
  hallway:         "1513694203232-719a280e022f",
  moodboard:       "1513519245088-0e12902e5a38",
  artDeco:         "1604147495798-57beb5d6af73",
  patternTextile:  "1583847268964-b28dc8f51f92",
  marbleCounter:   "1556909114-f6e7ad7d3136",
  sofaBeige:       "1493663284031-b7e3aefcae8e",
} as const;

export type PhotoKey = keyof typeof UNSPLASH_PHOTOS;

// Base URL is env-driven so it can be swapped for a self-hosted CDN.
const cdnBase = (import.meta.env["VITE_IMAGE_CDN"] as string | undefined) ?? "https://images.unsplash.com";

export function photo(key: PhotoKey, w = 640, h = 480): string {
  const id = UNSPLASH_PHOTOS[key];
  return `${cdnBase}/photo-${id}?auto=format&fit=crop&w=${w}&h=${h}&q=80`;
}

// Ontology-typed resolution chain:
//   1. SKU.attrs.photoUrl  — real catalog image (populated by C.0 importer)
//   2. SKU.attrs.category illustration — reliable inline-SVG by category
//      (zero CDN dependency, never 404s, deterministic)
// Unsplash IDs proved unreliable; the SVG illustrations from
// skuIllustrations.ts are the honest demo fallback until C.0 ships
// real PDP images.
import { illustrationForSku } from "./skuIllustrations";
import type { GraphNode } from "../types/ontology";

export function photoForSkuNode(sku: { label: string; attrs: Record<string, unknown> }): string {
  const real = sku.attrs["photoUrl"];
  if (typeof real === "string" && real.length > 0) return real;
  return illustrationForSku(sku as GraphNode);
}

// Deterministic per-SKU image — maps a SKU label to the most fitting photo.
export function photoForSku(label: string): string {
  const n = label.toLowerCase();
  if (n.includes("sofa"))                                  return photo("sofaBeige");
  if (n.includes("chandelier") || n.includes("pendant"))    return photo("chandelier");
  if (n.includes("countertop") || n.includes("quartz"))     return photo("marbleCounter");
  if (n.includes("kitchen") || n.includes("island"))        return photo("kitchenIsland");
  if (n.includes("bunk") || n.includes("kids"))             return photo("kidsRoom");
  if (n.includes("wardrobe") || n.includes("storage"))      return photo("wardrobe");
  if (n.includes("bed"))                                    return photo("bedroom");
  return photo("livingSofa");
}

export function photoForRoom(roomLabel: string): string {
  const n = roomLabel.toLowerCase();
  if (n.includes("living"))   return photo("livingSofa", 800, 560);
  if (n.includes("kitchen"))  return photo("kitchenModern", 800, 560);
  if (n.includes("kids"))     return photo("kidsRoom", 800, 560);
  if (n.includes("bedroom") || n.includes("master")) return photo("bedroom", 800, 560);
  if (n.includes("dining"))   return photo("diningWarm", 800, 560);
  if (n.includes("bath"))     return photo("bathroomLuxe", 800, 560);
  return photo("hallway", 800, 560);
}

export const HERO_PHOTO = photo("livingSofa", 1600, 900);

// Side-effect-free use of env so unused-import warnings don't trip on the
// CLAUDE.md rule that env.ts is the single source of truth.
export const __envSentinel = env.VITE_BRAND_NAME;
